import { AppBarProps, Drawer, Link } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { useEffect } from 'react'

import { authSlice } from '@/features/auth'
import { TenantSearchFormContext } from '@/features/tenant/components/TenantSearch/TenantSearchFormContext'
import { useGetTenantThemeQuery } from '@/services/api/tenant/tenantsEnhanced'
import { RootState, useTypedDispatch, useTypedSelector } from '@/store'
import { CompanyLogo, NavBar } from '../../../common/components'
import { useNavigate } from '../../../hooks'
import { CompanyLogoSmall } from '../AppToolBar/Components/CompanyLogo/CompanyLogoSmall'
import ImageToSvgConverter from '../ImageToSvgConverter/ImageToSvgConverter'
import { drawerRootStyles, drawerHeaderStyles, logoLinkStyles } from './SideBarStylesLegacy'

type SideBarProps = AppBarProps & {
  isOpen?: boolean
  isSidebarOpen?: boolean
  onDrawerToggle: () => void
  handleForceOpen: () => void
}

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}))

export const SideBarLegacy = (props: SideBarProps): JSX.Element => {
  const { isOpen, onDrawerToggle, handleForceOpen } = props
  const theme = useTheme()
  const navigate = useNavigate()
  const breakpointWidth = 1468

  const dispatch = useTypedDispatch()
  const accessedTenant = useTypedSelector((state: RootState) => state.authSlice.tenant)

  const { data: tenantTheme, refetch } = useGetTenantThemeQuery({
    tenantId: accessedTenant?.id,
  })

  useEffect(() => {
    if (tenantTheme) {
      dispatch(authSlice.actions.setTenantLogo(tenantTheme?.logo_url || ''))
      dispatch(authSlice.actions.setTenantThemeColor(tenantTheme.color || ''))
    }
  }, [dispatch, refetch, tenantTheme])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < breakpointWidth && isOpen) {
        onDrawerToggle()
      } else if (window.innerWidth >= breakpointWidth && !isOpen) {
        onDrawerToggle()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isOpen, onDrawerToggle])

  return (
    <Drawer
      anchor="left"
      variant="permanent"
      color="default"
      data-testid="drawer"
      open={isOpen}
      onClose={onDrawerToggle}
      ModalProps={{
        keepMounted: true,
      }}
      sx={drawerRootStyles(theme, isOpen ?? true)}>
      <DrawerHeader sx={drawerHeaderStyles(!!accessedTenant.logo_url)} data-testid="drawer-header">
        <Link
          color="inherit"
          underline="none"
          href="/"
          onClick={navigate}
          sx={logoLinkStyles(!!accessedTenant.logo_url)}>
          {isOpen ? (
            accessedTenant.logo_url ? (
              <ImageToSvgConverter imageString={accessedTenant.logo_url} />
            ) : (
              <CompanyLogo />
            )
          ) : accessedTenant.logo_url ? (
            <ImageToSvgConverter imageString={accessedTenant.logo_url} />
          ) : (
            <CompanyLogoSmall />
          )}
        </Link>
      </DrawerHeader>

      <TenantSearchFormContext>
        <NavBar isOpen={isOpen} handleForceOpen={handleForceOpen} />
      </TenantSearchFormContext>
    </Drawer>
  )
}
